import React from 'react';
import { AppInputTextFormik } from 'primitives/input/appInputText';

import { MaterialPhoto, PhotographerApplicationDTO } from 'appAPITypes';

import { AppInputSelectFormik } from 'primitives/input/appInputSelect';
import { Brands, dslrBrands } from '../joinPageConstants';
import { SwitchBoxFormik } from 'components/switch/switchBox';
import { AppInputTextareaFormik } from 'primitives/input/appInputTextarea';
import { MaterialRowYesNo } from '../formHelpers/materialRowYesNo';
import * as yup from 'yup';
import { isDev } from 'appEnv';
import { FormikPropsSlice } from 'primitives/input/formikUtils';
import { ShText } from '@shoootin/translations';
import { FrontCommonMessages } from 'appCommonTranslations';
import { getFrontMessage } from 'appMessageDefinitions';
import {
  ShCol,
  ShRow,
  ShSwitchBoxContainer,
  ShTitle,
} from '@shoootin/components-web';

export type MaterialPhotoSectionValues = Pick<
  PhotographerApplicationDTO,
  'hasPhoto' | 'photoMaterial'
>;

export const MaterialPhotoSectionValidation = yup
  .object()
  .shape<MaterialPhotoSectionValues>({
    photoMaterial: yup.object().shape<MaterialPhoto>({
      dslrCaseName: yup.string(),
      dslrBrandName: yup.string(),
      dslrCaseCustomName: yup.string(),
      hasTripod: yup.boolean(),
      hasWideAngle: yup.boolean(),
      whichWideAngle: yup.string(),
      has35mm: yup.boolean(),
      which35mm: yup.string(),
      has50mm: yup.boolean(),
      which50mm: yup.string(),
      photoComment: yup.string(),
      hasFlash: yup.boolean(),
      hasStudio: yup.boolean(),
      hasBackground: yup.boolean(),
    }),
    hasPhoto: yup.boolean(),
  });

export const EmptyMaterialPhotoSection: MaterialPhotoSectionValues = {
  hasPhoto: isDev,
  photoMaterial: {
    dslrBrandName: isDev ? '' : '',
    dslrCaseName: '',
    dslrCaseCustomName: '',
    hasTripod: false,
    hasWideAngle: false,
    whichWideAngle: '',
    has35mm: false,
    which35mm: '',
    has50mm: false,
    which50mm: '',
    photoComment: '',
    hasFlash: false,
    hasStudio: false,
    hasBackground: false,
  },
};

export const MaterialPhotoSection = ({
  form,
}: {
  form: FormikPropsSlice<keyof MaterialPhotoSectionValues>;
}) => {
  const photoMaterialBrandSelected = form.values.photoMaterial.dslrBrandName;
  const selectedDslrBrandArrayContainsCases =
    photoMaterialBrandSelected &&
    dslrBrands(form.values.photoMaterial.dslrBrandName) &&
    dslrBrands(form.values.photoMaterial.dslrBrandName).length > 0;

  const otherCaseNameSelected =
    selectedDslrBrandArrayContainsCases &&
    (form.values.photoMaterial.dslrCaseName === 'Autre' ||
      !dslrBrands(form.values.photoMaterial.dslrBrandName)[
        form.values.photoMaterial.dslrCaseName
      ]); // TODO Handle Other /Autre / Otras / Niecht / Ich liebe dich

  const selectedDslrBrandArrayDoesntContainCases =
    photoMaterialBrandSelected && !selectedDslrBrandArrayContainsCases;

  const selectedDslrBrandWithOtherCaseName =
    selectedDslrBrandArrayContainsCases && otherCaseNameSelected;

  const yesNoFields = [
    {
      name: 'photoMaterial.hasWideAngle',
      label: getFrontMessage(
        'form_prospectPhotographerPhotoMaterial_hasWideAngle_Label',
      ),
      precisionName: 'photoMaterial.whichWideAngle',
      onCheck: () => {
        // @ts-ignore
        form.setFieldTouched('photoMaterial.whichWideAngle', true);
        console.log('touched', form.touched);
      },
    },
    {
      name: 'photoMaterial.has35mm',
      label: getFrontMessage(
        'form_prospectPhotographerPhotoMaterial_has35mm_Label',
      ),
      precisionName: 'photoMaterial.which35mm',
    },
    {
      name: 'photoMaterial.has50mm',
      label: getFrontMessage(
        'form_prospectPhotographerPhotoMaterial_has50mm_Label',
      ),
      precisionName: 'photoMaterial.which50mm',
    },
    {
      name: 'photoMaterial.hasTripod',
      label: getFrontMessage(
        'form_prospectPhotographerPhotoMaterial_hasTripod_Label',
      ),
    },
    {
      name: 'photoMaterial.hasFlash',
      label: getFrontMessage(
        'form_prospectPhotographerPhotoMaterial_hasFlash_Label',
      ),
    },
    {
      name: 'photoMaterial.hasStudio',
      label: getFrontMessage(
        'form_prospectPhotographerPhotoMaterial_hasStudio_Label',
      ),
    },
    {
      name: 'photoMaterial.hasBackground',
      label: getFrontMessage(
        'form_prospectPhotographerPhotoMaterial_hasBackground_Label',
      ),
    },
  ];

  return (
    <SwitchBoxFormik
      form={form}
      name="hasPhoto"
      service="photo"
      label={
        <ShTitle size={4} color={form.values.hasPhoto ? 'white' : 'black'}>
          <ShText message={FrontCommonMessages.photo} />
        </ShTitle>
      }
    >
      <ShSwitchBoxContainer>
        <ShRow className="field-row wrap-s">
          <ShCol xs={60} md={20} lg={20}>
            <AppInputSelectFormik
              form={form}
              name="photoMaterial.dslrBrandName"
              options={Brands}
              placeholderMessage={getFrontMessage(
                'form_prospectPhotographerPhotoMaterial_dslrBrandName_PlaceHolder',
              )}
              onOptionSelected={(option) => {
                const valueHasChanged =
                  option.key !== form.values.photoMaterial.dslrBrandName;
                if (valueHasChanged) {
                  // @ts-ignore
                  form.setFieldValue('photoMaterial.dslrCaseName', '');
                }
              }}
            />
          </ShCol>
          {selectedDslrBrandArrayContainsCases && (
            <ShCol xs={60} md={20} lg={20}>
              <AppInputSelectFormik
                form={form}
                name="photoMaterial.dslrCaseName"
                options={dslrBrands(form.values.photoMaterial.dslrBrandName)}
                placeholderMessage={getFrontMessage(
                  'form_prospectPhotographerPhotoMaterial_dslrCaseName_PlaceHolder',
                )}
              />
            </ShCol>
          )}

          {selectedDslrBrandArrayDoesntContainCases && (
            <ShCol xs={60} md={20} lg={20}>
              <AppInputTextFormik
                form={form}
                name="photoMaterial.dslrCaseName"
                placeholderMessage={getFrontMessage(
                  'form_prospectPhotographerPhotoMaterial_dslrCaseName_PlaceHolder',
                )}
                modifier="large"
              />
            </ShCol>
          )}

          {selectedDslrBrandWithOtherCaseName && (
            <ShCol xs={60} md={20} lg={20}>
              <AppInputTextFormik
                form={form}
                name="photoMaterial.dslrCaseCustomName"
                placeholderMessage={getFrontMessage(
                  'form_prospectPhotographerPhotoMaterial_dslrCaseNameCustom_PlaceHolder',
                )}
                modifier="large"
              />
            </ShCol>
          )}
        </ShRow>

        {yesNoFields.map((field) => (
          <MaterialRowYesNo
            key={field.name}
            form={form}
            name={field.name}
            label={field.label}
            precisionName={field.precisionName}
            onCheck={() => field.onCheck && field.onCheck()}
            service={'photo'}
          />
        ))}

        <ShRow className="field-row gap">
          <ShCol xs={60}>
            <AppInputTextareaFormik
              name="photoMaterial.photoComment"
              form={form}
              placeholderMessage={getFrontMessage(
                'form_prospectPhotographerPhotoMaterial_photoComment_PlaceHolder',
              )}
              modifier="large"
            />
          </ShCol>
        </ShRow>
      </ShSwitchBoxContainer>
    </SwitchBoxFormik>
  );
};
